/**
 * InterestTypeField — interest type as a VISIBLE declared choice (amended AC-4,
 * operator 2026-08-26).
 *
 * The PRD's original AC-4 required no default and BLOCKED submit until the AM
 * picked. That was written before anyone checked what the automation does with
 * the choice: `repos/workflows/src/workflows/rpa/epic_additional_interest.py`
 * hardcodes `rpa_fill(value="MG")` plus a literal `role=option[name="Mortgagee"]`
 * click, and that is the ONLY interest-type call site in `src/` — no branch on
 * the value exists. A blocking dropdown would make the AM choose among values the
 * run ignores, filing every one as MG: a control that looks like it matters and
 * does not, which is the defect class this feature exists to remove.
 *
 * The operator's ruling KEEPS the dropdown and DROPS the blocking. The control
 * stays because it is the seam for a future rotation to more interest types —
 * the day the contract's enumeration grows, the form needs no change. So:
 *
 *  - the options are `binding.interestTypeValues`, read from the DECLARED
 *    enumeration and never hardcoded here;
 *  - the value arrives PRE-FILLED with the contract's documented default
 *    (`mortgagee` / EPIC code `MG`) and never blocks submit — a
 *    blocking-when-unset assertion is now a WRONG test;
 *  - on a change or delete the value is informational and read-only, so the
 *    declared type can never contradict the record already on the policy. Per the
 *    operator, EPIC leaves the type untouched on an update and removes the record
 *    on a delete, so nothing downstream reads it on those paths;
 *  - what the control DISPLAYS is always the value the action holds. Only
 *    `SELECTABLE_INTEREST_TYPES` is pickable (the RPA executes MG only), but a
 *    declared value outside that set — `additional_insured`, `loss_payee` — is
 *    rendered as its own disabled option rather than silently replaced by the
 *    selectable default. A control that shows "Mortgagee" while the payload
 *    ships something else is the same defect class as a control that does
 *    nothing, and `validateLaunch`'s membership check cannot catch it because
 *    the value IS in the declared enumeration.
 *
 * The enumeration may not outgrow the RPA silently. That guard is NOT here — it
 * is `launchSchema.conformance.test.ts`'s `PINNED_INTEREST_TYPE_VALUES`, which
 * fails product CI on a contract-side expansion, plus Q10 against
 * `action-list-contract` for the RPA half.
 */

import type { FieldSchema } from "~/common/workflows/schemaHelpers";
import { titleCaseKey } from "~/common/workflows/schemaHelpers";
import type { InterestOption } from "~/common/workflows/launch/interestTargetOptions";
import {
  COMPACT_FIELD_LABEL,
  COMPACT_INPUT,
  COMPACT_INPUT_TEXT,
  COMPACT_SINGLE_LINE,
} from "~/common/workflows/launch/launchDensity";
import { TYPE_BODY } from "~/common/workflows/paneTypography";

/**
 * The contract's documented default: "absent or empty means mortgagee" (the
 * declared description on `interest_type`, and `action-list-contract`'s
 * `test_absent_interest_type_defaults_to_mortgagee`).
 *
 * Resolved AGAINST the declared enumeration rather than written into the
 * payload as a literal: if a future contract drops `mortgagee`, the first
 * declared value is used and nothing invents a key the schema does not offer.
 */
export const CONTRACT_DEFAULT_INTEREST_TYPE = "mortgagee";

/** Epic two-letter codes shown in the control. Only Mortgagee is selectable. */
export const INTEREST_TYPE_EPIC_CODES: Readonly<Record<string, string>> = {
  mortgagee: "MG",
};

/** The one type the RPA can execute today. */
export const SELECTABLE_INTEREST_TYPES: readonly string[] = [
  CONTRACT_DEFAULT_INTEREST_TYPE,
];

/** AM-facing label, e.g. `Mortgagee (MG)`. */
export function interestTypeLabel(value: string): string {
  const code = INTEREST_TYPE_EPIC_CODES[value];
  const name = titleCaseKey(value);
  return code ? `${name} (${code})` : name;
}

/**
 * Label for a value the action ALREADY carries that the AM may not newly pick —
 * a declared interest type outside `SELECTABLE_INTEREST_TYPES`.
 *
 * It exists so the control can show the value it actually holds. Rendering
 * `interestTypeLabel` alone would read as an ordinary option; the suffix is what
 * makes the divergence legible rather than silent.
 */
export function unsupportedInterestTypeLabel(value: string): string {
  return `${interestTypeLabel(value)} — not available`;
}

/** The value the form pre-fills, or null when the schema declares no options. */
export function defaultInterestType(
  declared: readonly string[],
): string | null {
  if (declared.length === 0) return null;
  return declared.includes(CONTRACT_DEFAULT_INTEREST_TYPE)
    ? CONTRACT_DEFAULT_INTEREST_TYPE
    : declared[0];
}

/** Change types on which the interest type is derived, not chosen. */
function isDerivedChangeType(changeType: string): boolean {
  return changeType === "update" || changeType === "delete";
}

export interface InterestTypeFieldProps {
  field: FieldSchema;
  /** `binding.interestTypeValues` — the DECLARED enumeration, in schema order. */
  values: readonly string[];
  /** The action's current value for the declared interest-type key. */
  value: unknown;
  /** The action's declared change type ("" when the AM has not picked yet). */
  changeType: string;
  /** The selected target interest, when one is resolved. */
  target: InterestOption | null;
  disabled?: boolean;
  onChange: (next: string) => void;
}

export default function InterestTypeField({
  field,
  values,
  value,
  changeType,
  target,
  disabled = false,
  onChange,
}: InterestTypeFieldProps) {
  const fallback = defaultInterestType(values) ?? "";
  const shown = typeof value === "string" && value !== "" ? value : fallback;
  const derived = isDerivedChangeType(changeType);
  const id = `launch-interest-type-${field.key}`;
  const selectable = values.filter((v) =>
    SELECTABLE_INTEREST_TYPES.includes(v),
  );
  /**
   * The action carries a DECLARED type the AM may not newly pick — e.g. a
   * prefill of `additional_insured`, which the contract's enumeration allows and
   * `validateLaunch`'s membership check therefore accepts.
   *
   * The control used to substitute the selectable default here, so the AM saw
   * "Mortgagee (MG)" checked while the payload shipped `additional_insured`: a
   * dropdown that displays one value and submits another, with nothing —
   * validation included — to catch it. What renders is now ALWAYS `shown`; when
   * `shown` is outside the selectable set it gets its own disabled option, so
   * the value is visible, labelled as unavailable, and cannot be re-picked once
   * the AM moves off it.
   */
  const unsupported = shown !== "" && !selectable.includes(shown);

  const label = (
    <label htmlFor={id} className={COMPACT_FIELD_LABEL}>
      {field.label}
    </label>
  );

  if (derived) {
    return (
      <div className="flex flex-col gap-0.5">
        {label}
        <output
          id={id}
          className={`rounded-md border border-primary-gray-light-300 bg-primary-gray-light-50 px-2.5 py-1.5 ${COMPACT_INPUT_TEXT} text-primary-gray-light-700`}
          data-testid="launch-interest-type-readonly"
        >
          {interestTypeLabel(shown)}
        </output>
        <p className={`${TYPE_BODY} text-primary-gray-light-700`}>
          {target
            ? `Taken from ${target.name} — EPIC leaves the interest type unchanged on a change or delete.`
            : "Taken from the existing interest — EPIC leaves the interest type unchanged on a change or delete."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {label}
      <select
        id={id}
        value={shown}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`touch-target ${COMPACT_SINGLE_LINE}`}
        data-testid="launch-interest-type-select"
      >
        {unsupported && (
          <option key={shown} value={shown} disabled>
            {unsupportedInterestTypeLabel(shown)}
          </option>
        )}
        {selectable.map((v) => (
          <option key={v} value={v}>
            {interestTypeLabel(v)}
          </option>
        ))}
      </select>
      {unsupported && (
        <p
          className={`${TYPE_BODY} text-primary-gray-light-700`}
          data-testid="launch-interest-type-unsupported"
        >
          {`This action carries ${interestTypeLabel(shown)}, which the ` +
            `automation cannot file — it adds a Mortgagee (MG) record. Pick ` +
            `Mortgagee so the action says what the run will do.`}
        </p>
      )}
    </div>
  );
}
